import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  isStaffRole,
  roleHasPermission,
  type AuthUser,
  type LoginResponse,
  type Permission,
} from '@aforium/shared';
import { api, setAccessToken, setUnauthenticatedHandler } from '../lib/apiClient';

interface AuthContextValue {
  user: AuthUser | null;
  /** True until the initial silent refresh has settled. */
  isLoading: boolean;
  isAuthenticated: boolean;
  isStaff: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Capability check — the UI must use this instead of comparing roles. */
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session state for the whole app.
 *
 * On mount it attempts a silent refresh: the refresh token is an httpOnly
 * cookie, so a returning user is signed straight back in without ever having
 * put a token in localStorage.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    setUnauthenticatedHandler(clearSession);
    return () => setUnauthenticatedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = await api.refresh();
      if (cancelled) return;

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const me = await api.get<AuthUser>('/auth/me');
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setAccessToken(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthUser> => {
      const result = await api.post<LoginResponse>('/auth/login', { email, password });
      setAccessToken(result.accessToken);
      setUser(result.user);
      // A previous user's cached data must never leak into the new session.
      queryClient.clear();
      return result.user;
    },
    [queryClient],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* Signing out locally matters more than the server acknowledging it. */
    }
    clearSession();
  }, [clearSession]);

  const refreshUser = useCallback(async (): Promise<void> => {
    const me = await api.get<AuthUser>('/auth/me');
    setUser(me);
  }, []);

  const can = useCallback(
    (permission: Permission): boolean => (user ? roleHasPermission(user.role, permission) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      isStaff: user ? isStaffRole(user.role) : false,
      login,
      logout,
      refreshUser,
      can,
    }),
    [user, isLoading, login, logout, refreshUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }
  return context;
}

/** Narrowing helper for screens that only render behind a route guard. */
export function useCurrentUser(): AuthUser {
  const { user } = useAuth();
  if (!user) {
    throw new Error('useCurrentUser was called outside an authenticated route.');
  }
  return user;
}
